/**
 * Bilingual copy for the whole site (English + Arabic).
 *
 * The `Dict` interface is the single source of truth for the shape; both `en`
 * and `ar` are typed as `Dict`, so the compiler refuses to build if a key is
 * missing or mistyped in either language — they can never drift out of sync.
 *
 * Proper nouns that should NOT be translated (client company names, brand
 * logos, phone numbers, the email address) live in their components, not here.
 */

export type Lang = "en" | "ar";

type Member = { name: string; position: string };

export interface Dict {
  nav: {
    brand: string;
    brandSub: string;
    home: string;
    about: string;
    services: string;
    clients: string;
    team: string;
    contact: string;
    cta: string;
    /** Native name of the OTHER language — used as the toggle label */
    switchTo: string;
  };
  hero: {
    eyebrow: string;
    titlePre: string;
    titleHighlight: string;
    titlePost: string;
    tagline: string;
    paragraph: string;
    cta: string;
    sealed: string;
    sectors: string[];
    dash: {
      caseFile: string;
      confidential: string;
      verdict: string;
      percent: string;
    };
  };
  brief: {
    pre: string;
    highlight: string;
    post: string;
  };
  services: {
    eyebrow: string;
    title: string;
    items: string[];
  };
  about: {
    eyebrow: string;
    title: string;
    p1: string;
    p2: string;
  };
  clients: {
    eyebrow: string;
    title: string;
  };
  team: {
    eyebrow: string;
    title: string;
    members: Member[];
  };
  contact: {
    eyebrow: string;
    title: string;
    info: string;
    offices: string;
    labels: { phone: string; whatsapp: string; email: string };
    addresses: string[];
  };
  footer: {
    tagline: string;
    companyName: string;
    rights: string;
  };
}

const en: Dict = {
  nav: {
    brand: "Al-Rashad",
    brandSub: "Legal Services",
    home: "Home",
    about: "About",
    services: "Services",
    clients: "Clients",
    team: "Team",
    contact: "Contact",
    cta: "Book a Consultation",
    switchTo: "العربية",
  },
  hero: {
    eyebrow: "Basra · Est. Legal Counsel",
    titlePre: "Protect Your ",
    titleHighlight: "Rights",
    titlePost: ".",
    tagline: "Counsel you can trust. Justice you can feel.",
    paragraph:
      "A Basra-based firm in litigation, corporate, real estate, and commercial law — licensed, principled, and relentless in pursuit of the best outcome for every client.",
    cta: "Book a Consultation",
    sealed: "Sealed & Signed",
    sectors: [
      "Litigation",
      "Corporate",
      "Real Estate",
      "Banking",
      "Labor Law",
      "Taxes",
      "Trademarks",
      "Customs",
    ],
    dash: {
      caseFile: "Case File · AR-2026",
      confidential: "Confidential",
      verdict: "Verdict",
      percent: "100%",
    },
  },
  brief: {
    pre: "Our company was established in Basra after obtaining all licenses and official approvals — founded by lawyers who have dedicated themselves to providing the ",
    highlight: "best legal service in Iraq",
    post: " and the finest solutions for their clients.",
  },
  services: {
    eyebrow: "What We Practice",
    title: "Services",
    items: [
      "Retirement & Social Security",
      "Taxes",
      "Litigation in All Branches — Civil, Commercial, Labor, Criminal & Misdemeanour",
      "Customs Clearance, Customs Cases & Container Return to the Carrier Line",
      "Iraqi Company Formation & Foreign-Company Branches",
      "Companies, Agencies & Trademarks",
      "Commercial Banking Transactions",
      "Contracts",
      "Real Estate",
      "Conflict Resolution",
      "Work Permits for Foreigners",
      "Legal Translation",
      "Final Accounts",
      "Document Attestation — Iraqi Ministry of Foreign Affairs",
    ],
  },
  about: {
    eyebrow: "Who We Are",
    title: "About Us",
    p1: "Welcome to Al-Rashad, a reputable law firm based in Basra, Iraq. With our extensive legal expertise, we are committed to providing exceptional legal services to our clients.",
    p2: "Our team of highly skilled and experienced lawyers delivers personalized solutions tailored to each client. We combine in-depth knowledge of Iraqi law with a client-centric approach — handling every matter with professionalism, integrity, and the utmost confidentiality.",
  },
  clients: {
    eyebrow: "Trusted By",
    title: "Our Clients",
  },
  team: {
    eyebrow: "The People",
    title: "Our Team",
    members: [
      { name: "Duaa Adil Al-Mareany", position: "Managing Director" },
      {
        name: "An integrated group of elite lawyers and consultants",
        position: "Counsel & Advisors",
      },
    ],
  },
  contact: {
    eyebrow: "Get In Touch",
    title: "Contact Us",
    info: "Contact Info",
    offices: "Our Offices",
    labels: { phone: "Phone", whatsapp: "WhatsApp", email: "Email" },
    addresses: [
      "Iraq · Basra · Al Khaleej Quarter · neighborhood 3 / House 2O7L",
      "Baghdad / Al-Waziria",
      "Erbil / 100m Street",
    ],
  },
  footer: {
    tagline:
      "Al-Rashad for Legal Services and Customs Matters — Basra, Iraq.",
    companyName: "Al-Rashad for Legal Services",
    rights: "All rights reserved.",
  },
};

const ar: Dict = {
  nav: {
    brand: "الرشاد",
    brandSub: "للخدمات القانونية",
    home: "الرئيسية",
    about: "من نحن",
    services: "خدماتنا",
    clients: "عملاؤنا",
    team: "فريقنا",
    contact: "اتصل بنا",
    cta: "احجز استشارة",
    switchTo: "English",
  },
  hero: {
    eyebrow: "البصرة · مكتب محاماة معتمد",
    titlePre: "احمِ ",
    titleHighlight: "حقوقك",
    titlePost: ".",
    tagline: "مشورةٌ تثق بها، وعدالةٌ تشعر بها",
    paragraph:
      "مكتب محاماة في البصرة متخصص في التقاضي وقانون الشركات والعقارات والقانون التجاري — مرخّص، مبدئي، ولا يدّخر جهداً في تحقيق أفضل النتائج لكل عميل.",
    cta: "احجز استشارة",
    sealed: "موثّق وموقّع",
    sectors: [
      "التقاضي",
      "الشركات",
      "العقارات",
      "المصارف",
      "قانون العمل",
      "الضرائب",
      "العلامات التجارية",
      "الكمارك",
    ],
    dash: {
      caseFile: "ملف القضية · AR-2026",
      confidential: "سرّي",
      verdict: "الحكم",
      percent: "100%",
    },
  },
  brief: {
    pre: "تأسست شركتنا في البصرة بعد الحصول على كافة التراخيص والموافقات الرسمية — على يد محامين كرّسوا أنفسهم لتقديم ",
    highlight: "أفضل خدمة قانونية في العراق",
    post: " وأرقى الحلول لعملائهم.",
  },
  services: {
    eyebrow: "مجالات عملنا",
    title: "خدماتنا القانونية",
    items: [
      "التقاعد والضمان الاجتماعي",
      "الضرائب",
      "القضايا بجميع فروعها: المدنية والتجارية وقضايا العمل والجنايات والجنح",
      "التخليص الكمركي والقضايا الكمركية وإعادة الحاويات للخط الناقل",
      "تأسيس الشركات العراقية وفتح فروع الشركات الأجنبية",
      "الشركات والوكالات والعلامات التجارية",
      "المعاملات المصرفية التجارية",
      "العقود",
      "العقارات",
      "تسوية النزاعات",
      "إصدار إجازات عمل للأجانب",
      "الترجمة القانونية",
      "الحسابات الختامية",
      "تصديق المستندات من وزارة الخارجية العراقية",
    ],
  },
  about: {
    eyebrow: "من نحن",
    title: "نبذة عنّا",
    p1: "مرحباً بكم في الرشاد، مكتب محاماة مرموق مقره البصرة، العراق. بفضل خبرتنا القانونية الواسعة، نلتزم بتقديم خدمات قانونية استثنائية لعملائنا.",
    p2: "يضم فريقنا نخبة من المحامين ذوي المهارة والخبرة العالية الذين يقدّمون حلولاً مصمّمة خصيصاً لكل عميل. نجمع بين المعرفة العميقة بالقانون العراقي ونهج يركّز على العميل — ونتعامل مع كل قضية باحترافية ونزاهة وسرية تامة.",
  },
  clients: {
    eyebrow: "موضع ثقة",
    title: "عملاؤنا",
  },
  team: {
    eyebrow: "الفريق",
    title: "فريقنا",
    members: [
      { name: "دعاء عادل المرياني", position: "المدير المفوّض" },
      {
        name: "نخبة متكاملة من المحامين والمستشارين",
        position: "محامون ومستشارون",
      },
    ],
  },
  contact: {
    eyebrow: "ابقَ على تواصل",
    title: "اتصل بنا",
    info: "معلومات الاتصال",
    offices: "مكاتبنا",
    labels: { phone: "الهاتف", whatsapp: "واتساب", email: "البريد الإلكتروني" },
    addresses: [
      "العراق · البصرة · حي الخليج · محلة ٣ / دار 2O7L  (الفرع الرئيسي)",
      "بغداد / الوزيرية",
      "كربلاء والفرات الاوسط",
      "أربيل / شارع ١٠٠ متر",
    ],
  },
  footer: {
    tagline: "الرشاد للخدمات القانونية والامور الكمركية — البصرة، العراق.",
    companyName: "الرشاد للخدمات القانونية",
    rights: "جميع الحقوق محفوظة.",
  },
};

export const translations: Record<Lang, Dict> = { en, ar };
